import { default as Switch } from 'antd/es/switch';
import { useMemo, useState } from 'react';

import { useOpenNotification } from 'utils/antd/notification-toasts';
import { fieldToDisplayableName } from 'utils/formatters/fieldToDisplayableName';
import { formatDecimalAmount } from 'utils/formatters/formatCurrency';
import { virtualAccountNumberFormatter } from 'utils/formatters/virtualAccountNumberFormatter';
import { hasValue } from 'utils/hasValue';
import { logEventToHotjar } from 'utils/logging';

import { VALID_CREDITOR_TYPES } from '@pages/Receivers/ReceiverDetails/util';
import type { CreditorKeyValuePair, CreditorType } from 'api';
import {
  useGetDisbursementPayoutDraftsIdSummary,
  usePostCustomerCreditors
} from 'api';
import { Restricted } from 'containers/Restricted';

import type { SectionDefinition } from 'models/payout';

import { useOrganisationId, useVirtualAccounts } from 'hooks/context';
import { useFeatureAccessControl } from 'hooks/useFeatureAccessControl';
import { useGoogleAnalyticsDispatcher } from 'hooks/useGoogleAnalyticsDispatcher';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';

import { Country } from '@molecules/Country';

import { Loading } from 'components/pages/Loading';

import { SUMMARY_STEP_CONTENT_TEST_ID } from './constants';

import { CancelButton } from '../ConfirmCancelDialog';
import { SUBMIT_PAYOUT_TEST_ID } from '../constants';
import { ShowDataItem } from '../ShowDataItem';
import type { StepProps } from '../steps';

type Props = Omit<StepProps, 'responseField'> & {
  onCancel: () => void;
  draftId: number;
};

export const SummaryStep = ({ draftId, onSubmit, onCancel }: Props) => {
  const organisationId = useOrganisationId<number>();
  const openNotification = useOpenNotification();

  const {
    data: { data: data } = {},
    isSuccess,
    isLoading
  } = useGetDisbursementPayoutDraftsIdSummary(organisationId, draftId, {
    query: {
      refetchOnMount: true
    }
  });

  const { mutate: createCustomerCreditor } = usePostCustomerCreditors();

  const [saveReceiver, setSaveReceiver] = useState(false);

  const { isReceiverManagementEnabled, isLoading: featuresLoading } =
    useFeatureAccessControl();

  const [errors, setErrors] = useState<string[]>([]);

  const { findAccountByAccountNumber } = useVirtualAccounts();

  const account = useMemo(() => {
    if (hasValue(data?.payout_request?.virtual_account_number?.value)) {
      return findAccountByAccountNumber(
        data?.payout_request?.virtual_account_number?.value.toString()
      );
    }
  }, [data, findAccountByAccountNumber]);

  const website = useMemo(
    () => data?.debtor?.website?.value?.toString(),
    [data?.debtor?.website]
  );

  const dispatchGAEvent = useGoogleAnalyticsDispatcher();

  function transformCreditorDetails(
    section: string,
    details: SectionDefinition
  ): CreditorKeyValuePair[] {
    return Object.entries(details).map(([key, valueObj]) => ({
      key: `${section}.${key}`,
      value: valueObj.value.toString()
    }));
  }

  function extractCreditorFields(data: any, fields: string[]) {
    return fields.reduce(
      (acc, field) => {
        const value = data.creditor?.[field]?.value;
        if (value !== undefined) {
          acc[field === 'name' ? 'full_name' : field] = value.toString();
        }
        return acc;
      },
      {} as Record<string, string>
    );
  }

  function saveCreditorDetails() {
    if (saveReceiver && !featuresLoading && hasValue(data?.creditor)) {
      const creditorAccountDetails: CreditorKeyValuePair[] =
        transformCreditorDetails('creditor_account', data.creditor_account);

      const intermediaryAgentDetails =
        data.intermediary_agent &&
        transformCreditorDetails('intermediary_agent', data.intermediary_agent);

      const intermediaryAgentAccountDetails =
        data.intermediary_agent_account &&
        transformCreditorDetails(
          'intermediary_agent_account',
          data.intermediary_agent_account
        );

      const creditorDetails: CreditorKeyValuePair[] = creditorAccountDetails
        .concat(intermediaryAgentDetails || [])
        .concat(intermediaryAgentAccountDetails || []);

      const creditorType = data.creditor.type?.value.toString();
      const validCreditorType = VALID_CREDITOR_TYPES.includes(
        creditorType as CreditorType
      )
        ? (creditorType as CreditorType)
        : undefined;

      const fieldsToExtract = [
        'address_lines',
        'birthdate',
        'city',
        'country_code',
        'country_of_birth_code',
        'country_of_residence_code',
        'creditor_type',
        'email',
        'name',
        'id_expiry_date',
        'id_issuing_country_code',
        'id_issuing_date',
        'id_number',
        'id_type',
        'mobile_number',
        'postcode',
        'street_name',
        'state'
      ];

      const creditor = {
        ...extractCreditorFields(data, fieldsToExtract),
        creditor_type: validCreditorType,
        product_id: Number(data.product?.product_id),
        schema_id: Number(data.product?.schema_id),
        creditor_details: creditorDetails
      };

      return createCustomerCreditor({
        data: { creditor },
        organisationId: organisationId
      });
    }
  }

  const onSubmitHandler = async () => {
    logEventToHotjar('create-manual-payout:form-submit');

    dispatchGAEvent('create_manual_payout_submitted', {
      version: 'v1'
    });

    try {
      setErrors([]);
      await onSubmit(data);
      saveCreditorDetails();
    } catch (error: any) {
      if (error?.error[0]?.title === 'Precondition Failure') {
        setErrors(
          error.error.reduce(
            (
              acc: string[],
              error: { details: string[]; source: { pointer: string } }
            ) => {
              error.details.forEach((detail) => {
                if (typeof detail == 'string') {
                  acc.push(
                    `${fieldToDisplayableName(error.source.pointer)} ${detail}`
                  );
                } else if (typeof detail == 'object') {
                  Object.entries(detail).forEach(([_sectionKey, errors]) => {
                    if (typeof errors === 'object') {
                      Object.entries(
                        errors as Record<string, string[]>
                      ).forEach(([key, value]) => {
                        value.forEach((error: string) =>
                          acc.push(`${fieldToDisplayableName(key)} ${error}`)
                        );
                      });
                    }
                  });
                }
              });
              return acc;
            },
            []
          )
        );
      } else {
        openNotification({ kind: 'error', message: 'Error publishing draft' });
        onCancel();
        console.error(error);
      }
    }
  };

  return (
    <>
      {isSuccess && (
        <div className="px-10" data-testid={SUMMARY_STEP_CONTENT_TEST_ID}>
          <div className="flex flex-wrap gap-y-4">
            {account && (
              <ShowDataItem
                label="Account"
                className="w-1/2 grow even:text-right"
              >
                <p className="font-medium">
                  {account.name
                    ? account.name
                    : `${account.currency_name} account`}
                </p>
                <p className="text-xs">
                  No. {virtualAccountNumberFormatter(account?.account_number)}
                </p>
              </ShowDataItem>
            )}
            <ShowDataItem
              value={
                data?.payout_request?.amount?.value
                  ? formatDecimalAmount(
                      Number(data?.payout_request?.amount?.value)
                    )
                  : ''
              }
              label={fieldToDisplayableName('amount')}
              className="w-1/2 grow even:text-right"
            />
            <ShowDataItem
              value={data?.payout_request?.end_to_end_id?.value?.toString()}
              label={fieldToDisplayableName('end_to_end_id')}
              className="w-1/2 grow even:text-right"
            />
            <ShowDataItem
              value={data?.product?.local_instrument?.toString()}
              label="Selected product"
              className="w-1/2 grow even:text-right"
            />
            {website && (
              <ShowDataItem
                value={website}
                label="Website"
                className="w-1/2 grow even:text-right"
              />
            )}
          </div>
          <hr className="my-6" />
          <div>
            <h3 className="mb-4 font-medium">Sender</h3>
            <div className="flex flex-wrap gap-y-4">
              {Object.keys(data?.ultimate_debtor || {}).map((field: string) => {
                if (
                  field === 'country_code' &&
                  Boolean(data?.ultimate_debtor[field]?.value)
                ) {
                  return (
                    <Country
                      key={data?.ultimate_debtor[field]?.value}
                      countryIso={data?.ultimate_debtor[
                        field
                      ]?.value.toString()}
                    />
                  );
                }
                return (
                  <ShowDataItem
                    key={field}
                    value={(
                      data?.ultimate_debtor[field]?.value || ''
                    ).toString()}
                    label={fieldToDisplayableName(field)}
                    className="w-1/2 grow even:text-right"
                  />
                );
              })}
            </div>
          </div>
          <hr className="my-6" />
          <div>
            <h3 className="mb-4 font-medium">Receiver</h3>
            <div className="flex flex-wrap gap-y-4">
              {Object.keys(data?.creditor || {}).map((field: string) => {
                if (
                  field === 'country_code' &&
                  Boolean(data?.creditor[field]?.value)
                ) {
                  return (
                    <Country
                      key={data?.creditor[field]?.value}
                      countryIso={data?.creditor[field]?.value.toString()}
                    />
                  );
                }
                return (
                  <ShowDataItem
                    key={field}
                    value={(data?.creditor[field]?.value || '').toString()}
                    label={fieldToDisplayableName(
                      field,
                      'RECEIVER_FIELD_NAMES'
                    )}
                    className="w-1/2 grow even:text-right"
                  />
                );
              })}
            </div>
          </div>
          <hr className="my-6" />
          <div>
            <h3 className="mb-4 font-medium">Receiver Bank</h3>
            <div className="flex flex-wrap gap-y-4">
              {Object.keys(data?.creditor_account || {}).map(
                (field: string) => (
                  <ShowDataItem
                    key={field}
                    value={(
                      data?.creditor_account[field]?.value || ''
                    ).toString()}
                    label={fieldToDisplayableName(
                      field,
                      'RECEIVER_FIELD_NAMES'
                    )}
                    className="w-1/2 grow even:text-right"
                  />
                )
              )}
            </div>
          </div>
          {Boolean(Object.keys(data?.intermediary_agent ?? {}).length) && (
            <>
              <hr className="my-6" />
              <div>
                <h3 className="mb-4 font-medium">Intermediary Agent</h3>
                <div className="flex flex-wrap gap-y-4">
                  {Object.keys(data!.intermediary_agent!).map(
                    (field: string) => (
                      <ShowDataItem
                        key={field}
                        value={(
                          data?.intermediary_agent?.[field]?.value ?? ''
                        ).toString()}
                        label={fieldToDisplayableName(
                          field,
                          'RECEIVER_FIELD_NAMES'
                        )}
                        className="w-1/2 grow even:text-right"
                      />
                    )
                  )}
                </div>
              </div>
            </>
          )}
          {Boolean(
            Object.keys(data?.intermediary_agent_account ?? {}).length
          ) && (
            <>
              <hr className="my-6" />
              <div>
                <h3 className="mb-4 font-medium">Intermediary Agent Account</h3>
                <div className="flex flex-col gap-y-4">
                  {Object.keys(data!.intermediary_agent_account!).map(
                    (field: string) => (
                      <ShowDataItem
                        key={field}
                        value={(
                          data?.intermediary_agent_account?.[field]?.value ?? ''
                        ).toString()}
                        label={fieldToDisplayableName(
                          field,
                          'RECEIVER_FIELD_NAMES'
                        )}
                        className="w-full"
                      />
                    )
                  )}
                </div>
              </div>
            </>
          )}

          {isReceiverManagementEnabled && (
            <Restricted requiredPermission="receivers:manage">
              <div className="my-4 flex flex-row items-center justify-between">
                <p>Save receiver information</p>
                <Switch
                  checkedChildren="YES"
                  unCheckedChildren="NO"
                  checked={saveReceiver}
                  onChange={() => {
                    setSaveReceiver(!saveReceiver);
                  }}
                  data-testid="save-receiver-checkbox"
                />
              </div>
            </Restricted>
          )}

          <div className="my-6 flex gap-4">
            <CancelButton onAction={onCancel} />
            <PrimaryButton
              size="large"
              className="w-full"
              onClick={onSubmitHandler}
              data-testid={SUBMIT_PAYOUT_TEST_ID}
            >
              Submit
            </PrimaryButton>
          </div>
          {errors && (
            <div className="my-6">
              {errors.map((error, index) => (
                <div
                  key={index}
                  className="font-semibold italic text-feedback-error"
                >
                  {error}.
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {isLoading && <Loading />}
    </>
  );
};
